import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Resets UI state that a product tour opened as a side effect of a
 * `clickOnNext` step (e.g. an inline edit mode, a dialog) once the tour
 * steps past the target(s) that own it, or stops running altogether — so a
 * walkthrough can't leave the page in a half-opened state.
 *
 * The current step's selector comes from `state.productTour.currentStepTarget`,
 * which `JoyrideTourRunner` publishes as it moves, rather than from a
 * `toursRegistry` lookup here. This hook is reached from the app shell (the
 * profile menu in the sidebar), so a registry import would put all of the tour
 * prose in the initial bundle for every user on every page — see
 * `currentStepTarget` in `productTourSlice`.
 *
 * @param {string|string[]} targets - the step `target` selector(s) (as
 *   written in toursRegistry.js) that "own" this open state.
 * @param {() => void} resetFn - closes/reverts the state.
 */
const useResetOnTourStep = (targets, resetFn) => {
  const targetList = Array.isArray(targets) ? targets : [targets];

  const { isRunning, currentStepTarget } = useSelector(
    (state) => state.productTour,
  );

  const isOwnedNow = isRunning && targetList.includes(currentStepTarget);
  const wasOwnedRef = useRef(false);

  useEffect(() => {
    if (isOwnedNow) {
      wasOwnedRef.current = true;
      return;
    }
    if (wasOwnedRef.current) {
      wasOwnedRef.current = false;
      resetFn();
    }
    // resetFn is passed fresh on every render by callers — only react to
    // isOwnedNow flipping, not to identity changes of the callback.
  }, [isOwnedNow]);
};

export default useResetOnTourStep;
